import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import api from "../../lib/api";
import { errorMessage } from "../../lib/stays";

const tm = (v) => (v ? String(v).slice(0, 5) : "");

function Num({ label, k, form, set, step }) {
  return (
    <div><Label className="text-xs text-white/50">{label}</Label>
      <Input type="number" step={step} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" /></div>
  );
}
function Time({ label, k, form, set }) {
  return (
    <div><Label className="text-xs text-white/50">{label}</Label>
      <Input type="time" value={tm(form[k])} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" /></div>
  );
}
function Bool({ label, k, form, set }) {
  return (
    <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" className="accent-[#C9A24B] h-4 w-4" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} /> {label}</label>
  );
}

export default function AdminProperty({ property, onSaved }) {
  const [form, setForm] = useState(null);
  const [rules, setRules] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setRules((r) => ({ ...r, [k]: v }));

  useEffect(() => {
    if (!property) return;
    setForm(property);
    setRules(property.house_rules || {});
    api.get("/admin/policies").then((r) => setPolicies(r.data)).catch(() => {});
  }, [property]);

  if (!form) return <div className="text-white/40 py-10 text-center">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      const { units, house_rules, id, payment_qr, payment_mode, payment_upi_id, payment_whatsapp, qr_hold_minutes, payment_hold_minutes, ...payload } = form;   // payment fields live in the Payments tab
      const num = ["max_guests", "base_guests", "max_infants", "max_pets", "min_stay_nights", "max_stay_nights", "max_advance_days", "turnover_buffer_minutes", "min_hours", "max_hours", "pending_expiry_hours"];
      num.forEach((k) => { if (payload[k] !== null && payload[k] !== "") payload[k] = Number(payload[k]); });
      await api.patch(`/admin/properties/${property.id}`, payload);
      await api.patch(`/admin/properties/${property.id}/rules`, {
        ...rules,
        property_rules: Array.isArray(rules.property_rules) ? rules.property_rules : String(rules.property_rules || "").split("\n").filter(Boolean),
        important_information: Array.isArray(rules.important_information) ? rules.important_information : String(rules.important_information || "").split("\n").filter(Boolean),
      });
      toast.success("Property settings saved");
      onSaved();
    } catch (e) {
      const d = e.response?.data;
      toast.error(d && typeof d === "object" && !d.detail && !d.code ? Object.entries(d).map(([k, v]) => `${k}: ${[].concat(v).join(" ")}`).join(" · ") : errorMessage(e, "Couldn't save settings"));
    }
    setBusy(false);
  };

  const lines = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
  const box = "glass p-5 space-y-4";

  return (
    <div className="space-y-6" data-testid="admin-property">
      <div className={box}>
        <div className="eyebrow">Check-in & check-out</div>
        <div className="grid sm:grid-cols-4 gap-4">
          <Time label="Check-in time" k="check_in_time" form={form} set={set} />
          <Time label="Check-out time" k="check_out_time" form={form} set={set} />
          <Num label="Min stay (nights)" k="min_stay_nights" form={form} set={set} />
          <Num label="Max stay (nights)" k="max_stay_nights" form={form} set={set} />
          <Num label="Min hours" k="min_hours" form={form} set={set} />
          <Num label="Max hours" k="max_hours" form={form} set={set} />
          <Num label="Book up to (days ahead)" k="max_advance_days" form={form} set={set} />
          <Num label="Turnover buffer (min)" k="turnover_buffer_minutes" form={form} set={set} />
        </div>
        <div className="flex flex-wrap gap-6">
          <Bool label="Same-day turnover" k="allow_same_day_turnover" form={form} set={set} />
          <Bool label="Hourly bookings" k="allow_hourly" form={form} set={set} />
          <Bool label="Auto-confirm bookings" k="auto_confirm" form={form} set={set} />
        </div>
        <div className="grid sm:grid-cols-4 gap-4 border-t border-white/10 pt-4">
          <Bool label="Early check-in" k="early_checkin_enabled" form={form} set={set} />
          <Time label="Early from" k="early_checkin_time" form={form} set={set} />
          <Num label="Early fee" k="early_checkin_fee" form={form} set={set} />
          <span />
          <Bool label="Late check-out" k="late_checkout_enabled" form={form} set={set} />
          <Time label="Late until" k="late_checkout_time" form={form} set={set} />
          <Num label="Late fee" k="late_checkout_fee" form={form} set={set} />
        </div>
      </div>

      <div className={box}>
        <div className="eyebrow">Pricing</div>
        <div className="grid sm:grid-cols-4 gap-4">
          <Num label="Nightly rate" k="nightly_rate" form={form} set={set} />
          <Num label="Hourly rate" k="hourly_rate" form={form} set={set} />
          <Num label="Cleaning fee" k="cleaning_fee" form={form} set={set} />
          <Num label="Extra guest fee / night" k="extra_guest_fee" form={form} set={set} />
          <Num label="Service fee %" k="service_fee_percent" form={form} set={set} step="0.01" />
          <Num label="Tax %" k="tax_percent" form={form} set={set} step="0.01" />
        </div>
      </div>

      <div className={box}>
        <div className="eyebrow">Airbnb listing</div>
        <div><Label className="text-xs text-white/50">Airbnb listing link (shows a "Book on Airbnb" button on the Room and Booking pages)</Label>
          <Input value={form.airbnb_url || ""} onChange={(e) => set("airbnb_url", e.target.value.trim())} placeholder="https://www.airbnb.co.in/rooms/123456789"
            className="mt-1 bg-[#0f0f0f] border-white/10 h-10" data-testid="airbnb-url-input" /></div>
        <p className="text-xs text-white/40">On phones with the Airbnb app installed, tapping the button opens your listing in the app, with the guest's dates filled in. Leave empty to hide the button.</p>
      </div>

      <div className={box}>
        <div className="eyebrow">Guests</div>
        <div className="grid sm:grid-cols-4 gap-4">
          <Num label="Max guests" k="max_guests" form={form} set={set} />
          <Num label="Included in rate" k="base_guests" form={form} set={set} />
          <Num label="Max infants" k="max_infants" form={form} set={set} />
          <Num label="Max pets" k="max_pets" form={form} set={set} />
        </div>
        <div className="flex flex-wrap gap-6"><Bool label="Allow children" k="allow_children" form={form} set={set} /><Bool label="Allow infants" k="allow_infants" form={form} set={set} /><Bool label="Allow pets" k="allow_pets" form={form} set={set} /></div>
      </div>

      <div className={box}>
        <div className="eyebrow">Cancellation</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs text-white/50">Rate type</Label>
            <select value={form.refund_type} onChange={(e) => set("refund_type", e.target.value)} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm" data-testid="refund-type-select">
              <option value="REFUNDABLE">Refundable</option><option value="NON_REFUNDABLE">Non-refundable</option></select></div>
          <div><Label className="text-xs text-white/50">Cancellation policy</Label>
            <select value={form.cancellation_policy ?? ""} onChange={(e) => set("cancellation_policy", e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full bg-[#0f0f0f] border border-white/10 h-10 px-2 text-sm">
              <option value="">None</option>{policies.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.policy_type})</option>)}</select></div>
        </div>
        <p className="text-xs text-white/40">Tiers are edited under Django Admin → Cancellation policies (e.g. hours_before 168 → 100%, 72 → 50%). New bookings snapshot the policy; existing bookings keep theirs.</p>
      </div>

      <div className={box}>
        <div className="eyebrow">House rules</div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["early_checkin_policy", "Early check-in policy"], ["late_checkin_policy", "Late check-in policy"], ["late_checkout_policy", "Late check-out policy"],
            ["required_identification", "Required identification"], ["access_instructions", "Key / access instructions"], ["key_return_instructions", "Key return instructions"],
            ["guest_responsibilities", "Guest responsibilities"], ["property_condition_requirements", "Property condition requirements"]].map(([k, l]) => (
            <div key={k}><Label className="text-xs text-white/50">{l}</Label><Textarea value={rules[k] || ""} onChange={(e) => setR(k, e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 min-h-[70px]" /></div>))}
          <div><Label className="text-xs text-white/50">Minimum age</Label><Input type="number" value={rules.minimum_age ?? 18} onChange={(e) => setR("minimum_age", Number(e.target.value))} className="mt-1 bg-[#0f0f0f] border-white/10 h-10" /></div>
          <span />
          <div><Label className="text-xs text-white/50">Property rules (one per line)</Label><Textarea value={lines(rules.property_rules)} onChange={(e) => setR("property_rules", e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 min-h-[110px]" /></div>
          <div><Label className="text-xs text-white/50">Important information (one per line)</Label><Textarea value={lines(rules.important_information)} onChange={(e) => setR("important_information", e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 min-h-[110px]" /></div>
        </div>
      </div>

      <button disabled={busy} onClick={save} className="btn-gold disabled:opacity-40" data-testid="save-property">{busy ? "Saving…" : "Save property settings"}</button>
    </div>
  );
}
